import { yupResolver } from "@hookform/resolvers/yup";
import BreadcrumbWithLink from "components/Common/BreadcrumbWithLink";
import MessageFormValidate from "components/shared-components/MessageFormValidate";
import { formConvertErrorMessage, formValidate } from "helpers/form_helper";
import { useCreateShippingUnit } from "hooks";
import React from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { MetaTags } from "react-meta-tags";
import { Link, useNavigate } from "react-router-dom";
import Select from "react-select";
import { useToasts } from "react-toast-notifications";
import { Button, Card, CardBody, Col, FormGroup, Label, Row } from "reactstrap";
import * as yup from "yup";
import { getPhoneValidation } from "constants/validate";

const CreateShippingUnit = props => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation(["shippingUnit"]);
  const { addToast } = useToasts();

  const getInitDefaultValues = () => {
    return {
      name: "",
      phone_number: "",
      address: "",
      ward_id: undefined,
      district_id: undefined,
      province_id: undefined,
    };
  };

  const schema = yup.object().shape({
    name: yup
      .string()
      .required(t("form.validate.required", { title: t("form.name") }))
      .max(250, t("form.validate.char_max", { max: 250 })),
    address: yup
      .string()
      .required(t("form.validate.required", { title: t("form.address") }))
      .max(250, t("form.validate.char_max", { max: 250 })),
    phone_number: yup
      .string()
      .nullable()
      .required(t("form.validate.required", { title: t("form.phone_number") }))
      .matches(getPhoneValidation(t).regex, getPhoneValidation(t).errorMessage)
      .when(getPhoneValidation(t).when),
    ward_id: yup
      .object()
      .required(t("form.validate.select", { title: t("form.ward_id") })),
    district_id: yup
      .object()
      .required(t("form.validate.select", { title: t("form.district_id") })),
    province_id: yup
      .object()
      .required(t("form.validate.select", { title: t("form.province_id") })),
  });

  const {
    handleSubmit,
    reset,
    formState: { errors, touchedFields, isSubmitting },
    control,
    setValue,
    getValues,
    setError,
    register,
    unregister,
    trigger,
  } = useForm({
    resolver: yupResolver(schema),
    mode: "all",
    reValidateMode: "onChange",
    defaultValues: getInitDefaultValues(),
  });

  // Create Shipping Unit
  const onSuccessCreateShippingUnit = _data => {
    navigate("/shipping-unit/list");
  };
  const onErrorCreateShippingUnit = error => {
    if (error.code === 422 && error.fields.length > 0) {
      error.fields.forEach(item => {
        let fieldName = item.field;
        switch (item.field) {
          case "common":
            addToast(item.message, {
              appearance: "error",
              autoDismiss: true,
            });
            break;
          default:
            break;
        }
        setError(fieldName, {
          type: "manual",
          message: formConvertErrorMessage(item.message, t),
        });
      });
    }
  };
  const { isLoading, mutate: createShippingUnit } = useCreateShippingUnit(
    onSuccessCreateShippingUnit,
    onErrorCreateShippingUnit
  );

  const onSubmit = values => {
    const data = getValues();
    createShippingUnit({
      name: data.name,
      phone_number: data.phone_number,
      address: data.address,
      district_id: data.district_id.value,
      province_id: data.province_id.value,
      ward_id: data.ward_id.value,
    });
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <MetaTags>
          <title>{t("title.create")}</title>
        </MetaTags>
        <div className="container-fluid">
          <BreadcrumbWithLink
            title={t("title.create")}
            breadcrumbItems={[
              { name: t("title.manager") },
              { name: t("title.list"), link: "/shipping-unit/list" },
              { name: t("title.create") },
            ]}
          />

          <Row className="row-cols-1">
            <Col>
              <Card>
                <CardBody>
                  <Row className="row-cols-1 row-cols-md-2">
                    <Col>
                      <FormGroup className="mb-3">
                        <Label htmlFor="name">
                          {t("form.name")}{" "}
                          <span className="text-danger">*</span>
                        </Label>
                        <input
                          type="text"
                          className={`form-control ${formValidate(
                            errors,
                            touchedFields,
                            "name"
                          )}`}
                          placeholder={t("placeholder.enter", {
                            title: t("form.name"),
                          })}
                          {...register("name")}
                        />
                        {errors.name && (
                          <MessageFormValidate error={errors.name.message} />
                        )}
                      </FormGroup>
                    </Col>
                    <Col>
                      <FormGroup className="mb-3">
                        <Label htmlFor="phone_number">
                          {t("form.phone_number")}{" "}
                          <span className="text-danger">*</span>
                        </Label>
                        <input
                          type="text"
                          className={`form-control ${formValidate(
                            errors,
                            touchedFields,
                            "phone_number"
                          )}`}
                          placeholder={t("placeholder.enter", {
                            title: t("form.phone_number"),
                          })}
                          {...register("phone_number")}
                        />
                        {errors.phone_number && (
                          <MessageFormValidate
                            error={errors.phone_number.message}
                          />
                        )}
                      </FormGroup>
                    </Col>
                    <Col>
                      <FormGroup className="mb-3">
                        <Label htmlFor="address">
                          {t("form.address")}{" "}
                          <span className="text-danger">*</span>
                        </Label>
                        <input
                          type="text"
                          className={`form-control ${formValidate(
                            errors,
                            touchedFields,
                            "address"
                          )}`}
                          placeholder={t("placeholder.enter", {
                            title: t("form.address"),
                          })}
                          {...register("address")}
                        />
                        {errors.address && (
                          <MessageFormValidate error={errors.address.message} />
                        )}
                      </FormGroup>
                    </Col>
                    <Col>
                      <FormGroup className="mb-3">
                        <Label htmlFor="ward_id">
                          {t("form.ward_id")}{" "}
                          <span className="text-danger">*</span>
                        </Label>
                        <Controller
                          control={control}
                          name="ward_id"
                          render={({ field }) => (
                            <Select
                              {...field}
                              placeholder={t("placeholder.select", {
                                title: t("form.ward_id"),
                              })}
                              options={[
                                { value: "11001", label: "Phường 7" },
                                { value: "11002", label: "Phường 1" },
                                { value: "11003", label: "Phường 8" },
                              ]}
                              classNamePrefix="select2-selection"
                            />
                          )}
                        />
                        {errors.ward_id && (
                          <MessageFormValidate error={errors.ward_id.message} />
                        )}
                      </FormGroup>
                    </Col>
                    <Col>
                      <FormGroup className="mb-3">
                        <Label htmlFor="district_id">
                          {t("form.district_id")}{" "}
                          <span className="text-danger">*</span>
                        </Label>
                        <Controller
                          control={control}
                          name="district_id"
                          render={({ field }) => (
                            <Select
                              {...field}
                              placeholder={t("placeholder.select", {
                                title: t("form.district_id"),
                              })}
                              options={[
                                { value: "552", label: "Quận 1" },
                                { value: "553", label: "Quận 12" },
                                { value: "560", label: "Quận Phú Nhuận" },
                              ]}
                              classNamePrefix="select2-selection"
                            />
                          )}
                        />
                        {errors.district_id && (
                          <MessageFormValidate
                            error={errors.district_id.message}
                          />
                        )}
                      </FormGroup>
                    </Col>
                    <Col>
                      <FormGroup className="mb-3">
                        <Label htmlFor="province_id">
                          {t("form.province_id")}{" "}
                          <span className="text-danger">*</span>
                        </Label>
                        <Controller
                          control={control}
                          name="province_id"
                          render={({ field }) => (
                            <Select
                              {...field}
                              placeholder={t("placeholder.select", {
                                title: t("form.province_id"),
                              })}
                              options={[
                                { value: "1", label: "Thành phố Hà Nội" },
                                { value: "50", label: "Thành phố Hồ Chí Minh" },
                              ]}
                              classNamePrefix="select2-selection"
                            />
                          )}
                        />
                        {errors.province_id && (
                          <MessageFormValidate
                            error={errors.province_id.message}
                          />
                        )}
                      </FormGroup>
                    </Col>
                  </Row>
                  <Row className="justify-content-end">
                    <Col className="col-auto">
                      <Link
                        to="/shipping-unit/list"
                        className="btn btn-danger btn-label"
                      >
                        <i className="fas fa-undo label-icon"></i>
                        {t("form.cancel")}
                      </Link>
                    </Col>

                    <Col className="col-auto">
                      <Button
                        color="primary"
                        className="btn-label"
                        type="button"
                        onClick={() => {
                          handleSubmit(onSubmit)();
                        }}
                        disabled={isLoading}
                      >
                        <i className="bx bx-save label-icon"></i>
                        {t("form.save")}
                      </Button>
                    </Col>
                  </Row>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </div>
      </div>
    </React.Fragment>
  );
};

export default CreateShippingUnit;
